/**
 * @fileoverview Factory for creating base data sources with standard CRUD
 * operations. Handles API normalization, validation, and caching for entity
 * data sources.
 */

import { GridColDef } from '@mui/x-data-grid';
import { z } from 'zod';
import { DataField, DataModel, DataModelId, DataSource, OmitId } from './types';
import { DataSourceCache } from './cache';
import { apiClient } from './api-client';
import { EntityType, getCache } from './cache-registry';
import { TIME_OFFSET_MS } from './constants';
import { createAuditFields, createAuditFieldsWithArchive, createLookupEntityFields } from './field-factories';
import { BaseEntity, DataSourceMutatedEvent, DataSourceMutatedEventDetail } from './types';
import { createZodValidator } from './validation-utils';

/**
 * JSON primitive types for API normalization.
 */
type JsonPrimitive = string | number | boolean | null;

/**
 * Recursive JSON value type.
 */
type JsonValue = JsonPrimitive | JsonObject | JsonArray;

/**
 * JSON object type for API payloads.
 */
interface JsonObject {
  [key: string]: JsonValue;
}

/**
 * JSON array type.
 */
type JsonArray = JsonValue[];

/**
 * Default date field names that require timezone normalization.
 * Can be overridden per data source via dateFieldsForNormalization config.
 */
const DEFAULT_DATE_FIELDS_FOR_NORMALIZATION: readonly string[] = ['recorded_date', 'dob'];

/**
 * Creates a type guard to check if a field name requires date normalization.
 * @param dateFields - Array of field names to check against
 */
function createDateFieldChecker(dateFields: readonly string[]): (key: string) => boolean {
  const fieldSet = new Set(dateFields);
  return (key: string): boolean => fieldSet.has(key);
}

/**
 * Dispatches a typed data source mutation event.
 */
function dispatchMutationEvent(detail: DataSourceMutatedEventDetail): void {
  if (typeof window !== 'undefined') {
    const event: DataSourceMutatedEvent = new CustomEvent('data-source-mutated', { detail });
    window.dispatchEvent(event);
  }
}

/**
 * Normalizes a Date to YYYY-MM-DD string with timezone offset.
 */
function normalizeDateValue(value: Date): string {
  const newDate = new Date(value.getTime() + TIME_OFFSET_MS.TWO_HOURS);
  return newDate.toISOString().slice(0, 10);
}

/**
 * Normalizes an ISO date string to YYYY-MM-DD with timezone offset.
 * Returns original value if not a valid ISO date string.
 */
function normalizeIsoDateString(value: string): string {
  if (!/T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)) {
    return value;
  }

  const dateObj = new Date(value);
  if (isNaN(dateObj.getTime())) {
    return value;
  }

  return normalizeDateValue(dateObj);
}

/**
 * Creates a normalizer function for date fields in API requests.
 * @param dateFields - Array of field names that should have date normalization applied
 */
function createApiNormalizer(dateFields: readonly string[]): (obj: JsonObject) => JsonObject {
  const isDateField = createDateFieldChecker(dateFields);

  function normalizeForApi(obj: JsonObject): JsonObject {
    const out: JsonObject = {};

    for (const [key, value] of Object.entries(obj)) {
      if (isDateField(key)) {
        if (value instanceof Date) {
          out[key] = normalizeDateValue(value);
        } else if (typeof value === 'string') {
          out[key] = normalizeIsoDateString(value);
        } else {
          out[key] = value;
        }
      } else if (value && typeof value === 'object' && !Array.isArray(value)) {
        out[key] = normalizeForApi(value as JsonObject);
      } else {
        out[key] = value;
      }
    }
    return out;
  }

  return normalizeForApi;
}

/**
 * Configuration for creating a base data source.
 */
export interface BaseDataSourceConfig<T extends BaseEntity> {
  /** API endpoint path (e.g., 'note', 'tag') */
  endpoint: string;
  /** Grid column definitions for the data grid */
  fields: GridColDef[];
  /** Zod validation schema for the entity (output type must match T, input can differ for defaults) */
  schema: z.ZodType<T, z.ZodTypeDef, unknown>;
  /** Field to search on (defaults to 'name') */
  searchField?: string;
  /**
   * Date field names that require timezone normalization during API requests.
   * These fields will have their values converted from local time to UTC.
   * @default ['recorded_date', 'dob']
   */
  dateFieldsForNormalization?: readonly string[];
}

/**
 * Entity type with required ID for data grid compatibility.
 */
export type EntityWithId<T extends BaseEntity> = T & { id: DataModelId };

/**
 * API request parameters for getMany pagination and filtering.
 * Different from types/index.ts GetManyParams which uses MUI grid models.
 */
interface ApiGetManyParams {
  page: string;
  pageSize: string;
  sortField?: string;
  sortOrder?: string;
  search?: string;
}

/**
 * A {@link DataSource} that is guaranteed to expose the full CRUD + validate
 * surface. The base `DataSource` type leaves every method optional, but
 * `createBaseDataSource` always provides them, so the return type marks them
 * required — callers (and tests) can invoke them without null checks.
 */
export type FullDataSource<D extends DataModel> = DataSource<D> &
  Required<Pick<DataSource<D>, 'getMany' | 'getOne' | 'createOne' | 'updateOne' | 'deleteOne' | 'validate'>>;

/**
 * Creates a data source with standard CRUD operations for an entity type.
 *
 * @template T - Entity type extending BaseEntity
 * @param config - Configuration for the data source
 * @returns Fully typed DataSource with CRUD operations
 */
export function createBaseDataSource<T extends BaseEntity>(
  config: BaseDataSourceConfig<T>
): FullDataSource<EntityWithId<T>> {
  const {
    endpoint,
    fields,
    schema,
    searchField = 'name',
    dateFieldsForNormalization = DEFAULT_DATE_FIELDS_FOR_NORMALIZATION,
  } = config;

  const normalizeForApi = createApiNormalizer(dateFieldsForNormalization);

  return {
    fields: fields as DataField[],

    getMany: async ({ paginationModel, filterModel, sortModel }) => {
      try {
        const params: ApiGetManyParams = {
          page: paginationModel.page.toString(),
          pageSize: paginationModel.pageSize.toString(),
        };

        if (sortModel?.length) {
          const { field, sort } = sortModel[0];
          params.sortField = field;
          params.sortOrder = sort ?? 'asc';
        }

        if (filterModel?.items?.length) {
          const searchFilter = filterModel.items.find(item => item.field === searchField);
          if (searchFilter?.value) {
            params.search = String(searchFilter.value);
          }
        }

        const data = await apiClient.getMany<EntityWithId<T>>(endpoint, params);
        return {
          items: data.items,
          itemCount: data.itemCount,
        };
      } catch (error) {
        console.error(`Error fetching from ${endpoint} table:`, error);
        throw new Error(`Failed to fetch ${endpoint} table`);
      }
    },

    getOne: async (id: DataModelId): Promise<EntityWithId<T>> => {
      try {
        return await apiClient.getOne<EntityWithId<T>>(endpoint, String(id));
      } catch (error) {
        console.error(`Error fetching ${endpoint}:`, error);
        throw error;
      }
    },

    createOne: async (data: Partial<OmitId<EntityWithId<T>>>): Promise<EntityWithId<T>> => {
      try {
        const normalized = normalizeForApi(data as JsonObject);
        const result = await apiClient.createOne<EntityWithId<T>, JsonObject>(endpoint, normalized);
        dispatchMutationEvent({
          endpoint,
          action: 'create',
          id: result.id != null ? String(result.id) : undefined,
        });
        return result;
      } catch (error) {
        console.error(`Error creating ${endpoint}:`, error);
        throw error;
      }
    },

    updateOne: async (id: DataModelId, data: Partial<OmitId<EntityWithId<T>>>): Promise<EntityWithId<T>> => {
      try {
        const payload = normalizeForApi(data as JsonObject);
        const result = await apiClient.updateOne<EntityWithId<T>, JsonObject>(endpoint, String(id), payload);
        dispatchMutationEvent({ endpoint, action: 'update', id: String(id) });
        return result;
      } catch (error) {
        console.error(`Error updating ${endpoint}:`, error);
        throw error;
      }
    },

    deleteOne: async (id: DataModelId): Promise<void> => {
      try {
        await apiClient.deleteOne(endpoint, String(id));
        dispatchMutationEvent({ endpoint, action: 'delete', id: String(id) });
      } catch (error) {
        console.error(`Error deleting ${endpoint}:`, error);
        throw error;
      }
    },

    validate: createZodValidator(schema),
  };
}

// =============================================================================
// Lookup Entity Data Source Factory
// =============================================================================

/**
 * Configuration for creating a lookup entity data source.
 * Lookup entities are simple reference tables with id, name, description,
 * display_order, and metadata fields.
 */
export interface LookupDataSourceConfig<T extends BaseEntity> {
  /** Entity type identifier matching the API endpoint */
  endpoint: EntityType;
  /** Zod validation schema for the entity (output type must match T, input can differ for defaults) */
  schema: z.ZodType<T, z.ZodTypeDef, unknown>;
  /** Whether to include archived_at field (default: false) */
  includeArchive?: boolean;
}

/**
 * Result of createLookupDataSource containing the data source and cache.
 */
export interface LookupDataSourceResult<T extends BaseEntity> {
  dataSource: FullDataSource<EntityWithId<T>>;
  cache: DataSourceCache;
}

/**
 * Factory function for creating data sources for simple lookup/reference entities.
 * These entities share a common structure: id, name, description, display_order,
 * metadata, and standard audit fields.
 *
 * Use this for entities like: area, category, kind, list, tag.
 * Do NOT use for entities with foreign keys or custom fields.
 *
 * @template T - Entity type extending BaseEntity
 * @param config - Configuration for the lookup data source
 * @returns Object containing dataSource and cache
 *
 * @example
 * ```typescript
 * const { dataSource: areaDataSource, cache: areaCache } = createLookupDataSource({
 *   endpoint: 'area',
 *   schema: AreaSchema,
 *   includeArchive: true,
 * });
 * ```
 */
export function createLookupDataSource<T extends BaseEntity>(
  config: LookupDataSourceConfig<T>
): LookupDataSourceResult<T> {
  const { endpoint, schema, includeArchive = false } = config;

  const dataSource = createBaseDataSource<T>({
    endpoint,
    searchField: 'name',
    schema,
    fields: [
      ...createLookupEntityFields(),
      ...(includeArchive ? createAuditFieldsWithArchive() : createAuditFields()),
    ],
  });

  const cache = getCache(endpoint);

  return { dataSource, cache };
}
